import React from 'react'
import { X, Mail, BookOpen, Calendar, Users, Clock, GraduationCap, Target, IndianRupee, Home, Brain } from 'lucide-react'
import { cn, formatDate } from '@/lib/utils'

interface StudentDetailModalProps {
  student: any
  isOpen: boolean
  onClose: () => void
}

export const StudentDetailModal: React.FC<StudentDetailModalProps> = ({
  student,
  isOpen,
  onClose
}) => {
  if (!isOpen || !student) return null

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'inactive': return 'bg-red-100 text-red-800'
      case 'graduated': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Student Details
              </h3>
              <button
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Profile Section */}
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 relative">
                  {student.profilePicture ? (
                    <img
                      className="h-16 w-16 rounded-full object-cover"
                      src={student.profilePicture}
                      alt={`${student.firstName} ${student.lastName}`}
                      onError={(e) => {
                        // Replace broken image with initials fallback
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="h-16 w-16 bg-primary rounded-full flex items-center justify-center"
                    style={{ display: student.profilePicture ? 'none' : 'flex' }}
                  >
                    <span className="text-white font-medium text-lg">
                      {student.firstName?.[0] || 'S'}{student.lastName?.[0] || 'U'}
                    </span>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900">
                    {student.firstName} {student.lastName}
                  </h4>
                  <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getStatusColor(student.status))}>
                    {student.status}
                  </span>
                </div>
              </div>

              {/* Contact Information */}
              <div className="border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h5>
                <div className="space-y-2">
                  {student.email && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Mail className="h-4 w-4" />
                      <span>{student.email}</span>
                    </div>
                  )}
                  {/* {student.phone && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Phone className="h-4 w-4" />
                      <span>{student.phone}</span>
                    </div>
                  )} */}
                </div>
              </div>

              {/* Academic Information */}
              <div className="border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-900 mb-3">Academic Information</h5>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <BookOpen className="h-4 w-4" />
                    <span>Grade {student.grade}</span>
                  </div>
                  {student.schoolName && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Users className="h-4 w-4" />
                      <span>{student.schoolName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Session Statistics */}
              <div className="border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-900 mb-3">Session Statistics</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">{student.totalSessions}</p>
                    <p className="text-xs text-gray-600">Total Sessions</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-lg font-semibold text-gray-900">{student.upcomingSessions}</p>
                    <p className="text-xs text-gray-600">Upcoming</p>
                  </div>
                </div>
                {student.lastSession && (
                  <div className="mt-3">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>Last session: {formatDate(student.lastSession)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Conversation Summary */}
              {student.conversationSummary && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">
                    <Brain className="inline h-4 w-4 mr-2" />
                    Conversation Summary
                  </h5>
                  <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                    {student.conversationSummary}
                  </p>
                </div>
              )}

              {/* Academic Performance */}
              {(student.academicPercentage || student.testScores) && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">
                    <GraduationCap className="inline h-4 w-4 mr-2" />
                    Academic Performance
                  </h5>
                  <div className="space-y-2">
                    {student.academicPercentage && (
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Academic Percentage:</span> {student.academicPercentage}%
                      </div>
                    )}
                    {student.testScores && Object.keys(student.testScores).length > 0 && (
                      <div className="space-y-1">
                        <span className="text-sm font-medium text-gray-700">Test Scores:</span>
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          {student.testScores.jee && (
                            <div className="text-xs bg-gray-50 px-2 py-1 rounded">
                              JEE: {student.testScores.jee} percentile
                            </div>
                          )}
                          {student.testScores.neet && (
                            <div className="text-xs bg-gray-50 px-2 py-1 rounded">
                              NEET: {student.testScores.neet}
                            </div>
                          )}
                          {student.testScores.sat && (
                            <div className="text-xs bg-gray-50 px-2 py-1 rounded">
                              SAT: {student.testScores.sat}
                            </div>
                          )}
                          {student.testScores.cat && (
                            <div className="text-xs bg-gray-50 px-2 py-1 rounded">
                              CAT: {student.testScores.cat} percentile
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* College Preferences */}
              {(student.targetCourses?.length > 0 || student.preferredTiers?.length > 0 ||
                student.preferredStates?.length > 0 || student.preferredCities?.length > 0) && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">
                    <Target className="inline h-4 w-4 mr-2" />
                    College Preferences
                  </h5>
                  <div className="space-y-3">
                    {student.targetCourses?.length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500">Target Courses:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {student.targetCourses.map((course: string, idx: number) => (
                            <span key={idx} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">
                              {course}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {student.preferredTiers?.length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500">Preferred Tiers:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {student.preferredTiers.map((tier: string, idx: number) => (
                            <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                              {tier.replace('tier_', 'Tier ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {student.preferredStates?.length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500">Preferred States:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {student.preferredStates.slice(0, 5).map((state: string, idx: number) => (
                            <span key={idx} className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full">
                              {state}
                            </span>
                          ))}
                          {student.preferredStates.length > 5 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{student.preferredStates.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {student.preferredCities?.length > 0 && (
                      <div>
                        <span className="text-xs text-gray-500">Preferred Cities:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {student.preferredCities.slice(0, 5).map((city: string, idx: number) => (
                            <span key={idx} className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded-full">
                              {city}
                            </span>
                          ))}
                          {student.preferredCities.length > 5 && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                              +{student.preferredCities.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Budget & Accommodation */}
              {(student.budgetRange || student.accommodationPreference) && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">
                    <IndianRupee className="inline h-4 w-4 mr-2" />
                    Budget & Accommodation
                  </h5>
                  <div className="space-y-2">
                    {student.budgetRange && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <IndianRupee className="h-4 w-4" />
                        <span>
                          Budget: ₹{(student.budgetRange.min / 100000).toFixed(1)}L - ₹{(student.budgetRange.max / 100000).toFixed(1)}L per year
                        </span>
                      </div>
                    )}
                    {student.accommodationPreference && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Home className="h-4 w-4" />
                        <span>
                          Accommodation: {student.accommodationPreference === 'day_scholar' ? 'Day Scholar' :
                                         student.accommodationPreference === 'hostel' ? 'Hostel' : 'Any'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Student Persona */}
              {student.studentPersona && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">
                    <Users className="inline h-4 w-4 mr-2" />
                    Student Persona
                  </h5>
                  <div className="space-y-3 text-sm">
                    {student.studentPersona.communicationStyle && (
                      <div>
                        <span className="font-medium text-gray-700">Communication Style:</span>
                        <p className="text-gray-600 mt-1">{student.studentPersona.communicationStyle}</p>
                      </div>
                    )}
                    {student.studentPersona.emotionalTone && (
                      <div>
                        <span className="font-medium text-gray-700">Emotional Tone:</span>
                        <p className="text-gray-600 mt-1">{student.studentPersona.emotionalTone}</p>
                      </div>
                    )}
                    {student.studentPersona.aspirations && student.studentPersona.aspirations.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700">Aspirations:</span>
                        <ul className="list-disc list-inside text-gray-600 mt-1">
                          {student.studentPersona.aspirations.map((asp: string, idx: number) => (
                            <li key={idx}>{asp}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {student.studentPersona.concerns && student.studentPersona.concerns.length > 0 && (
                      <div>
                        <span className="font-medium text-gray-700">Concerns:</span>
                        <ul className="list-disc list-inside text-gray-600 mt-1">
                          {student.studentPersona.concerns.map((con: string, idx: number) => (
                            <li key={idx}>{con}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Interests */}
              {student.interests && student.interests.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h5 className="text-sm font-medium text-gray-900 mb-3">Interests</h5>
                  <div className="flex flex-wrap gap-2">
                    {student.interests.map((interest: string, index: number) => (
                      <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Account Information */}
              <div className="border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-900 mb-3">Account Information</h5>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>Joined: {formatDate(student.joinedDate)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}